import apiService from "./configAxios";
import { PublicApi } from "../constants/apiRoutes";
import { hasStatisticsConsent } from "./cookieConsent";

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY_MS = 250;

const hasWindow = () => typeof window !== "undefined";

export const sanitizeClientErrorUrl = (rawUrl) => {
  if (!rawUrl) return null;

  try {
    const url = new URL(rawUrl);
    const tokenLikeName =
      /(token|capability|credential|secret|signature|authorization)/i;
    Array.from(url.searchParams.keys()).forEach((parameterName) => {
      if (tokenLikeName.test(parameterName)) {
        url.searchParams.delete(parameterName);
      }
    });
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    Array.from(hashParams.keys()).forEach((parameterName) => {
      if (tokenLikeName.test(parameterName)) {
        hashParams.delete(parameterName);
      }
    });
    const remainingHash = hashParams.toString();
    url.hash = remainingHash ? `#${remainingHash}` : "";
    return url.toString();
  } catch {
    // Never fall back to logging an unparsed URL that may contain a bearer token.
    return null;
  }
};

const getStorage = () => {
  if (!hasWindow()) {
    return null;
  }

  try {
    return window.localStorage;
  } catch (err) {
    return null;
  }
};

const logClientTrackingFailure = (eventName, error, payload) => {
  if (!eventName || !hasWindow()) {
    return;
  }

  const body = {
    event: eventName,
    message: error?.message || String(error || "Unknown dataLayer error"),
    url: sanitizeClientErrorUrl(window.location?.href),
    payload,
  };

  try {
    if (window.navigator?.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], {
        type: "application/json",
      });

      if (window.navigator.sendBeacon(PublicApi.trackingClientError(), blob)) {
        return;
      }
    }
  } catch (err) {
    // Fall back to Axios below.
  }

  apiService.post(PublicApi.trackingClientError(), body).catch(() => {});
};

export const pushDataLayerEvent = (payload, options = {}) => {
  const {
    dedupKey,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    logFailure = true,
  } = options;

  if ((!payload?.event && !payload?.user_id) || !hasWindow()) {
    return Promise.resolve(false);
  }

  const storage = getStorage();
  if (dedupKey && storage?.getItem(dedupKey)) {
    return Promise.resolve(false);
  }

  let attempt = 0;

  return new Promise((resolve) => {
    const tryPush = () => {
      attempt += 1;

      try {
        window.dataLayer = Array.isArray(window.dataLayer)
          ? window.dataLayer
          : [];

        if (typeof window.dataLayer.push !== "function") {
          throw new Error("window.dataLayer.push is not available");
        }

        window.dataLayer.push(payload);
        if (dedupKey) {
          storage?.setItem(dedupKey, new Date().toISOString());
        }
        resolve(true);
      } catch (err) {
        if (attempt < maxAttempts) {
          window.setTimeout(tryPush, retryDelayMs);
          return;
        }

        if (logFailure) {
          logClientTrackingFailure(payload.event || "user_id", err, payload);
        }
        resolve(false);
      }
    };

    tryPush();
  });
};

export const setAnalyticsUserId = (userId) => {
  if (!hasWindow() || typeof window.gtag !== "function") {
    return false;
  }

  const isLogout = userId === null;
  if (
    !isLogout &&
    (userId === undefined || userId === "" || !hasStatisticsConsent())
  ) {
    return false;
  }

  try {
    window.gtag("set", {
      user_id: isLogout ? null : String(userId),
    });
    return true;
  } catch (err) {
    return false;
  }
};

export const pushUserData = (user, event = "user_data") => {
  const userId = user?.id;
  if (!userId) {
    return Promise.resolve(false);
  }

  const payload = event
    ? { event, user_id: String(userId) }
    : { user_id: String(userId) };

  setAnalyticsUserId(userId);

  return pushDataLayerEvent(payload, {
    logFailure: false,
  });
};

export const calculateLifetimeValue = (payments = []) =>
  payments
    .filter((payment) => payment?.status === "SUCCESS")
    .reduce((sum, payment) => sum + Number(payment?.amount || 0), 0);
